import { Card, OverlayTrigger, Popover } from "react-bootstrap"
import { RightShipVesselInfo } from "../hooks/usePrevetGetRightShipData"
import rightShipLogo from "../../../assets/images/right-ship.svg"
import { PrevetRightShipBadgeIcon } from "./PrevetRightShipBadgeIcon"

function getDocSafetyScore(
  rightShipVesselInfo: RightShipVesselInfo
): number | undefined {
  const doctSafetyScoreObject =
    rightShipVesselInfo?.safetyScore?.additionalData.find(
      (data) => data.key === "docSafetyScore"
    )

  return doctSafetyScoreObject?.value
}

function getGhgVerified(rightShipVesselInfo: RightShipVesselInfo): boolean {
  const isVerifiedObject = rightShipVesselInfo?.ghg?.additionalData.find(
    (data) => data.key === "verified"
  )

  return isVerifiedObject?.value as boolean
}

function getGhgEvdi(rightShipVesselInfo: RightShipVesselInfo): number {
  const isVerifiedObject = rightShipVesselInfo?.ghg?.additionalData.find(
    (data) => data.key === "evdi"
  )

  const value = isVerifiedObject?.value as number

  return Number(value.toFixed(2))
}

function getGhgPlus(rightShipVesselInfo: RightShipVesselInfo): boolean {
  const plusObject = rightShipVesselInfo?.ghg?.additionalData.find(
    (data) => data.key === "plus"
  )

  return plusObject?.value as boolean
}

function getGhgIconColors(rating: string | number) {
  switch (rating) {
    case "A":
      return { fill: "#00763B", font: "#FFFFFF" }
    case "B":
      return { fill: "#62BA46", font: "#001728" }
    case "C":
      return { fill: "#AED36B", font: "#001728" }
    case "D":
      return { fill: "#FFDD15", font: "#001728" }
    case "E":
      return { fill: "#FFAA1C", font: "#001728" }
    case "F":
      return { fill: "#FC6B37", font: "#001728" }
    case "G":
      return { fill: "#D71920", font: "#001728" }
    default:
      return { fill: "#d9d9d9", font: "#000000" }
  }
}

const renderTooltip = () => {
  return (
    <Popover id="popover-basic">
      <Popover.Body>Data from selected date is unavailable.</Popover.Body>
    </Popover>
  )
}

export function getScoreFontColor(score: number, tag?: string) {
  switch (score) {
    case 5:
      return "#00763B"
    case 4:
      return "#62BA46"
    case 3:
      return "#AED36B"
    case 2:
      return "#FFDD15"
    case 1:
      return "#D71920"
    case 0:
      return "#F7661F"
    default:
      return "#FFFFFFF"
  }
}

function GhgIcon({
  rating,
  plus,
}: {
  rating: string
  plus: boolean | undefined
}) {
  return (
    <div className="PrevetRightShip_GhgIcon">
      <div
        className="PrevetRightShip_GhgIcon-Letter"
        style={{ color: getGhgIconColors(rating).font }}
      >
        {rating === "U" ? (
          <span
            style={{ fontSize: "1rem", position: "relative", left: "-1px" }}
          >
            N/A
          </span>
        ) : (
          <span>{rating}</span>
        )}
        {plus && <span>+</span>}
      </div>
      <svg
        className="PrevetRightShip_GhgIcon-Svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 56.7 40"
      >
        <polygon
          fill={getGhgIconColors(rating).fill}
          points="0,0 0,40 42.5,40 56.7,20 42.5,0"
        />
      </svg>
    </div>
  )
}

export function PrevetRightShip({
  rightShipVesselInfo,
  dateSelected,
}: {
  rightShipVesselInfo: RightShipVesselInfo
  dateSelected: string
}) {
  const docSafetyScore = getDocSafetyScore(rightShipVesselInfo)

  const isValidSafetyScore =
    rightShipVesselInfo.safetyScore &&
    Number(rightShipVesselInfo.safetyScore.safetyScore) > 0

  return (
    <Card className="PrevetContainer_RightShip">
      {rightShipVesselInfo.cached ? (
        <>
          <div className="d-flex" style={{ position: "absolute" }}>
            <div className="text-muted mt-3" style={{ fontSize: "0.8rem" }}>
              {formatDateToLongString(new Date().toISOString().split("T")[0])}
              <OverlayTrigger placement="top" overlay={renderTooltip()}>
                <i
                  className="ti-info-alt ms-1"
                  style={{ position: "relative", fontSize: "0.65rem" }}
                />
              </OverlayTrigger>
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className="text-muted mt-3"
            style={{ fontSize: "0.8rem", position: "absolute" }}
          >
            {formatDateToLongString(dateSelected)}
          </div>
        </>
      )}
      <div className="d-flex mt-4">
        <img
          className="PrevetRightShip_Logo"
          src={rightShipLogo}
          alt="right-ship-logo"
        />
        <PrevetRightShipBadgeIcon
          rightShipInspection={rightShipVesselInfo.rightshipInspection}
          platformLink={rightShipVesselInfo.platformLink}
        />
      </div>

      {/* Table */}
      <table className="w-100 mt-2 me-4 ms-2">
        <thead />
        <tbody>
          <tr className="w-100">
            <td>
              {" "}
              <h3 className="PrevetRightShip_Heading">Safety Score</h3>
            </td>
            <td className="d-flex justify-content-center">
              {isValidSafetyScore ? (
                <>
                  <span
                    className="PrevetRightShip_Score"
                    style={{
                      color: `${getScoreFontColor(
                        rightShipVesselInfo.safetyScore?.safetyScore
                      )}`,
                    }}
                  >
                    {rightShipVesselInfo.safetyScore?.safetyScore}
                  </span>
                  <span className="PrevetRightShip_Score--sm">/5</span>
                </>
              ) : (
                <span className="PrevetRightShip_Score--sm">N/A</span>
              )}
            </td>
          </tr>
          <tr className="w-100">
            <td>
              <h3 className="PrevetRightShip_Heading">DOC Subscore</h3>
            </td>
            <td className="d-flex justify-content-center">
              {docSafetyScore ? (
                <>
                  <span
                    className="PrevetRightShip_Score"
                    style={{
                      color: `${getScoreFontColor(
                        Number(docSafetyScore),
                        "docSafetyScore"
                      )}`,
                    }}
                  >
                    {docSafetyScore}
                  </span>
                  <span className="PrevetRightShip_Score--sm">/5</span>
                </>
              ) : (
                <span className="PrevetRightShip_Score--sm">N/A</span>
              )}
            </td>
          </tr>
          <tr className="w-100">
            <td>
              <h3 className="PrevetRightShip_Heading">GHG Rating</h3>
            </td>
            <td>
              <div className="d-flex flex-column gap-2 align-items-center">
                <GhgIcon
                  rating={rightShipVesselInfo.ghg.rating}
                  plus={getGhgPlus(rightShipVesselInfo)}
                />
                <div className="PrevetRightShip_Evdi">
                  {getGhgVerified(rightShipVesselInfo) ? (
                    <div style={{ color: "#00763B", fontWeight: "bold" }}>
                      {getGhgVerified(rightShipVesselInfo) && "VERIFIED"}
                    </div>
                  ) : (
                    <div style={{ fontWeight: "bold" }}>
                      {!getGhgVerified(rightShipVesselInfo) && "UNVERIFIED"}
                    </div>
                  )}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  )
}

function formatDateToLongString(dateString: string): string {
  // Parse the input date string into a Date object
  const date = new Date(dateString)

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format. Expected format: YYYY-MM-DD")
  }

  // Define an array of month names
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  // Extract the month, day, and year
  const month = monthNames[date.getMonth()] // Months are 0-indexed
  const day = date.getDate()
  const year = date.getFullYear()

  // Return the formatted string
  return `${month} ${day}, ${year}`
}
